import { AlertTriangle, Loader2, MailCheck } from "lucide-react";
import { useState } from "react";

import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { Link, useQuery } from "@/lib/router";

export function Join() {
  const query = useQuery();
  const expired = query.get("expired") === "1";

  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setState("sending");
    setError(null);
    try {
      await api.post("/api/auth/request", { email });
      setState("sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setState("error");
    }
  }

  if (state === "sent") {
    return (
      <Shell>
        <div className="flex min-h-[70vh] flex-col justify-center py-16">
          <div className="grid size-12 place-items-center rounded-2xl border border-brand/25 bg-brand/10 text-brand">
            <MailCheck className="size-6" />
          </div>
          <h1 className="mt-6 text-3xl font-semibold tracking-[-0.025em]">Check your email.</h1>
          <p className="mt-4 max-w-lg text-[15.5px] leading-relaxed text-muted-foreground">
            If <span className="text-foreground">{email}</span> can receive mail, there's a link
            on its way. Open it and you'll be signed in on this device for the next 90 days.
          </p>
          <p className="mt-3 max-w-lg text-[14px] leading-relaxed text-muted-foreground">
            The link works once and expires in an hour. Nothing here tells you whether that
            address was already registered — that's deliberate.
          </p>
          <div className="mt-8">
            <Button
              variant="outline"
              className="rounded-full px-6"
              onClick={() => setState("idle")}
            >
              Use a different address
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex min-h-[70vh] flex-col justify-center py-16">
        <h1 className="text-4xl font-semibold tracking-[-0.03em] sm:text-5xl">
          One address, and
          <br />
          <span className="text-gradient">that's the signup.</span>
        </h1>
        <p className="mt-5 max-w-lg text-[15.5px] leading-relaxed text-muted-foreground">
          No password. We email you a link; opening it signs you in on this device and keeps you
          signed in. Use the same address later and you'll land back on the same profile.
        </p>

        {expired && (
          <div className="mt-7 flex max-w-lg items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 p-4">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <p className="text-[14px] leading-relaxed text-muted-foreground">
              That link had already been used or had expired. They last an hour and work once —
              ask for a fresh one below.
            </p>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 max-w-lg">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="h-11 flex-1 bg-card/60 text-[15px]"
            />
            <Button
              type="submit"
              size="lg"
              disabled={state === "sending"}
              className="h-11 rounded-full px-7"
            >
              {state === "sending" ? (
                <>
                  <Loader2 className="animate-spin" /> Sending…
                </>
              ) : (
                "Send me a link"
              )}
            </Button>
          </div>

          {error && <p className="mt-3 text-[14px] text-destructive">{error}</p>}

          <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">
            We use your address to send that link, to tell you about a match, and to pass on
            contact details when two people both say yes. There is no newsletter to unsubscribe
            from because there is no newsletter.
          </p>
        </form>

        <p className="mt-10 text-[13.5px] text-muted-foreground">
          <Link href="/" className="underline underline-offset-4 hover:text-foreground">
            What is this?
          </Link>
        </p>
      </div>
    </Shell>
  );
}
